import { Type } from '@sinclair/typebox';
import { FormatRegistry } from '@sinclair/typebox';
// Register the 'uuid' and 'date-time' formats with TypeBox at module-load time.
// Fastify routes validate via Ajv (which has ajv-formats), so route schemas
// work without this. But standalone `Value.Check(schema, x)` (e.g. the runtime's
// `tryParseMemoryEnvelope` in `packages/hub/agent/memory/confirm.ts`) uses
// TypeBox's own validator, which silently ignores unrecognized formats and
// will REJECT any value as "Unknown format" — making every well-formed
// envelope fail validation. Registering here covers every consumer of the
// shared schemas through a single import side effect.
// RFC 4122 — accepts every UUID version including the nil UUID. Semantic
// gating (e.g. rejecting the nil UUID for non-nullable foreign keys) is the
// caller's job, not the format validator's. Single canonical home for the
// UUID format check: the `'uuid'` FormatRegistry entry below AND
// `isCanonicalUuid` both derive from this one literal — do not add copies.
const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
if (!FormatRegistry.Has('uuid')) {
    FormatRegistry.Set('uuid', (value) => uuidRegex.test(value));
}
if (!FormatRegistry.Has('date-time')) {
    // RFC 3339 / ISO 8601 — same shape Date.prototype.toISOString() produces.
    FormatRegistry.Set('date-time', (value) => !Number.isNaN(Date.parse(value)));
}
if (!FormatRegistry.Has('uri')) {
    // Pragmatic URI check matching Ajv-formats' permissive behavior for the
    // `uri` format: any string the WHATWG `URL` parser accepts (absolute URL).
    // Fastify routes still validate via ajv-formats; this entry is only consulted
    // by standalone `Value.Check(schema, x)` callers in @verybigthings/trackframe-shared
    // (e.g. `validateTrackframeConfig`, which would otherwise reject every URL
    // as "Unknown format" — same trap the `uuid` / `date-time` entries above fix).
    FormatRegistry.Set('uri', (value) => {
        try {
            new URL(value);
            return true;
        }
        catch {
            return false;
        }
    });
}
/**
 * Canonical-form RFC-4122 UUID check: anchored 8-4-4-4-12 hex with hyphens,
 * case-insensitive, version-agnostic (v1–v8 and the nil UUID all pass).
 * This is a FORMAT gate, not a version or existence gate — same predicate
 * that backs the `'uuid'` FormatRegistry entry / `UuidSchema`.
 */
export function isCanonicalUuid(value) {
    return uuidRegex.test(value);
}
export const UuidSchema = Type.String({ format: 'uuid' });
export const IsoDateTimeSchema = Type.String({ format: 'date-time' });
export const JsonObjectSchema = Type.Record(Type.String(), Type.Unknown());
/**
 * Standard error response shape used across the hub.
 * `error` is a stable machine-readable code, `message` is human-readable.
 */
export const ErrorResponseSchema = Type.Object({
    error: Type.String(),
    message: Type.String(),
});
//# sourceMappingURL=common.js.map